import { z } from 'zod'

/** The three worlds a process can live in (PROMPT.md §5.4). */
export const workloadKindSchema = z.enum(['pm2', 'docker', 'systemd'])
export type WorkloadKind = z.infer<typeof workloadKindSchema>

export const workloadStatusSchema = z.enum([
  'running',
  'stopped',
  'restarting',
  'errored',
  /** systemd knows units that are neither running nor failed, e.g. `exited` oneshots. */
  'inactive',
  'unknown',
])
export type WorkloadStatus = z.infer<typeof workloadStatusSchema>

export const workloadItemSchema = z.object({
  kind: workloadKindSchema,
  /** Stable handle for actions: pm2 name, container id, unit name. */
  id: z.string(),
  name: z.string(),
  status: workloadStatusSchema,
  /** Raw status word from the tool, shown when it says more than our five categories. */
  statusText: z.string(),
  cpuPct: z.number().nonnegative().nullable(),
  memBytes: z.number().nonnegative().nullable(),
  uptimeSec: z.number().nonnegative().nullable(),
  restarts: z.number().int().nonnegative().nullable(),
  /** Compose project for docker, the pm2 user for pm2, the unit's description for systemd. */
  group: z.string().nullable(),
  detail: z.string().nullable(),
})
export type WorkloadItem = z.infer<typeof workloadItemSchema>

export const workloadSectionSchema = z.object({
  kind: workloadKindSchema,
  /** Null when the section loaded; a German message when the listing itself failed. */
  error: z.string().nullable(),
  items: z.array(workloadItemSchema),
  /**
   * For pm2: the user whose process list this is. pm2 is per user, so a root login sees nothing
   * of what `deploy` runs unless we go and look (§5.4).
   */
  runAsUser: z.string().nullable(),
})
export type WorkloadSection = z.infer<typeof workloadSectionSchema>

export const workloadListSchema = z.object({
  serverId: z.string(),
  sections: z.array(workloadSectionSchema),
})

export const workloadActionSchema = z.enum(['start', 'stop', 'restart'])
export type WorkloadAction = z.infer<typeof workloadActionSchema>

export const workloadActionInputSchema = z.object({
  kind: workloadKindSchema,
  id: z.string().min(1),
  action: workloadActionSchema,
  /** Answer for `sudo_mode = 'ask'`, kept only for this request. */
  sudoPassword: z.string().optional(),
})

export const workloadActionResultSchema = z.object({
  ok: z.boolean(),
  /** What to show the user, in German. */
  message: z.string(),
  exitCode: z.number().int().nullable(),
  output: z.string(),
})
export type WorkloadActionResult = z.infer<typeof workloadActionResultSchema>

/** Ring buffer size in the frontend (§5.5). */
export const LOG_BUFFER_LINES = 5000

export const logStreamQuerySchema = z.object({
  kind: workloadKindSchema,
  id: z.string().min(1),
  /** How many past lines to fetch before following. */
  tail: z.coerce.number().int().min(0).max(1000).default(200),
})

/** Control frames on the log socket; the log lines themselves travel as plain text frames. */
export const logSocketMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('started'), command: z.string() }),
  z.object({ type: z.literal('ended'), reason: z.string() }),
  z.object({ type: z.literal('error'), message: z.string() }),
])
export type LogSocketMessage = z.infer<typeof logSocketMessageSchema>

export type WorkloadActionInput = z.input<typeof workloadActionInputSchema>
